import React from 'react';

import { deprecate } from 'react-is-deprecated';

import PropTypes from 'prop-types';

import { createChainedFunction } from '@atc/create-chained-function';
import { useFeatures } from '@atc/features';

import { Address } from 'reaxl';
import { OwnerPhoneNumber } from 'reaxl-molecules';

const renderOwnerPhoneNumber = (props) => (
    <div className="margin-bottom-3 margin-bottom-md-0">
        <OwnerPhoneNumber
            {...props}
        />
    </div>
);

/**
 *  The PrivateOwnerDetails component is a component to display data pertinent to the private seller.
 *  If the data is present, it will also display the seller name, address, and phone number.
 *
 */
function PrivateOwnerDetails({
    listingId,
    owner,
    fireTelmetricsTag = () => {}, // deprecated
    phoneClickHandler,
    className = null,
    ...rest
}) {
    const { call: [isCallEnabled] } = useFeatures(['call']);

    const { name, location, phone, id } = owner;

    // deprecate fireTelmetricsTag
    phoneClickHandler = createChainedFunction(phoneClickHandler, fireTelmetricsTag);

    const renderAddress = () => {
        const { address } = location;
        delete address.address1;
        delete address.address2;
        return address && (
            <Address
                location={address}
                className="padding-bottom-3 display-block"
                inline
            />
        );
    };

    return (
        <div
            data-cmp="privateOwnerDetails"
            className={className}
            {...rest}
        >
            <h2 className="text-bold padding-bottom-3">
                {name}
            </h2>
            {location && renderAddress()}
            {isCallEnabled && phone && renderOwnerPhoneNumber({
                phone,
                isCallable: true,
                analyticsData: {
                    inventoryId: listingId,
                    ownerId: id,
                },
                phoneClickHandler,
            })}
        </div>
    );
}

PrivateOwnerDetails.propTypes = {
    /**
    * The className to apply to the root node
    */
    className: PropTypes.string,
    /**
     * If true, a user can click phone number and make a call.
     */
    isPhoneNumberCallable: PropTypes.bool,
    /**
     * Id of relevant listing. Can be string for BAP preorder
     */
    listingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * The Owner shape contains all the display data
     */
    owner: PropTypes.shape({
        /**
         * The name will display in the header of the ItemCard
         */
        name: PropTypes.string,
        /**
         * The location object contains the location data for the Address component
         */
        location: PropTypes.object,
        /**
         * The phone object contains the phone number for the owner.
         */
        phone: PropTypes.object,
        /**
         * Id for owner
         */
        id: PropTypes.number,
    }).isRequired,
    /**
      * The callback function fires when owner phone is called replacing fireTelmetricsTag
      */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text or call CTA is clicked
     */
    fireTelmetricsTag: deprecate(PropTypes.func, 'fireTelmetricsTag is deprecated in OwnerDetails. Use phoneClickHandler instead'),
};

export default PrivateOwnerDetails;
